import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import { PawPrint, Camera, ChevronLeft } from "lucide-react";

const FIELDS = [
  { name: "name", label: "Nome", required: true },
  { name: "breed", label: "Razza" },
  { name: "sex", label: "Sesso", type: "select", options: ["", "Maschio", "Femmina"] },
  { name: "birth_date", label: "Data di nascita", type: "date" },
  { name: "color", label: "Colore mantello" },
  { name: "weight_kg", label: "Peso (kg)", type: "number" },
  { name: "height_cm", label: "Altezza al garrese (cm)", type: "number" },
  { name: "microchip", label: "Numero microchip", private: true, hint: "Non è mai visibile ad altri utenti. Serve solo per identificazione ufficiale in caso di smarrimento." },
  { name: "distinctive_traits", label: "Tratti distintivi", multiline: true },
  { name: "medical_notes", label: "Note mediche / patologie", multiline: true, private: true, hint: "Le patologie del tuo cane restano visibili solo a te e ai veterinari autorizzati." },
  { name: "vaccinations", label: "Vaccinazioni effettuate", multiline: true },
  { name: "emergency_contact", label: "Contatto di emergenza (telefono)", private: true, hint: "Il telefono è visibile solo in caso di SOS attivo, non nel passaporto pubblico." },
];

const INPUT_CLS =
  "mt-2 w-full px-4 py-3 rounded-xl bg-white border border-[#F0EBE3] outline-none text-[#1A1A1A] placeholder:text-[#B5AE9E] focus:border-[#FF7A1A] focus:ring-2 focus:ring-[#FF7A1A]/20 transition-all";
const LABEL_CLS =
  "text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-semibold";

export default function DogForm() {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dogId) {
      api.get(`/dogs/${dogId}`).then((r) => {
        setData(r.data);
        setPhotoPreview(r.data.photo_url);
      });
    } else {
      // Check for stub
      api.get("/dogs").then((r) => {
        const stub = r.data.find((d) => d.is_stub);
        if (stub) setData({ name: stub.name });
      });
    }
  }, [dogId]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB)");
    try {
      const dataUrl = await compressImage(f, { maxSide: 1280, quality: 0.85 });
      setPhotoPreview(dataUrl);
      setData((d) => ({ ...d, photo_url: dataUrl }));
    } catch (err) {
      console.warn("compress error", err);
      toast.error("Impossibile leggere la foto. Riprova con un'altra.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...data };
      if (payload.weight_kg) payload.weight_kg = parseFloat(payload.weight_kg);
      if (payload.height_cm) payload.height_cm = parseFloat(payload.height_cm);
      if (dogId) await api.put(`/dogs/${dogId}`, payload);
      else await api.post("/dogs", payload);
      toast.success("Passaporto salvato");
      navigate("/app/cani");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-[calc(env(safe-area-inset-bottom,0px)+120px)] md:pb-10">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate("/app/cani")}
        data-testid="dog-form-back"
        className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> I miei cani
      </button>

      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FF7A1A] mb-2">
        <PawPrint className="h-3.5 w-3.5" /> Passaporto digitale
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">
        {dogId ? "Modifica scheda" : "Nuovo cane"}
      </h1>
      <p className="text-[#5C5C5C] mt-2 text-sm sm:text-base">
        Compila tutti i campi per attivare il QR Code e il sistema di emergenza.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        {/* Photo block */}
        <div
          className="rounded-3xl p-5 flex gap-5 items-center bg-white border border-[#F0EBE3]"
          style={{ boxShadow: "0 8px 24px -10px rgba(0,0,0,0.08)" }}
        >
          <div className="h-24 w-24 rounded-2xl overflow-hidden bg-[#FAF7F0] border border-[#F0EBE3] flex items-center justify-center shrink-0">
            {photoPreview ? (
              <img src={photoPreview} alt="" className="w-full h-full object-cover" />
            ) : (
              <Camera className="h-7 w-7 text-[#C9C0AC]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#1A1A1A]">Foto del cane</div>
            <div className="text-xs text-[#9A9A9A] mb-2">JPG/PNG · ottimizzata automaticamente</div>
            <input
              data-testid="dog-photo"
              type="file"
              accept="image/*"
              onChange={onPhoto}
              className="text-xs text-[#5C5C5C] file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-[#FAF7F0] file:text-[#1A1A1A] file:text-xs file:font-medium hover:file:bg-[#F4EFE6] file:cursor-pointer"
            />
          </div>
        </div>

        {/* Fields */}
        <div
          className="rounded-3xl p-5 sm:p-6 bg-white border border-[#F0EBE3]"
          style={{ boxShadow: "0 8px 24px -10px rgba(0,0,0,0.08)" }}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.multiline ? "sm:col-span-2" : ""}>
                <label className={LABEL_CLS + " flex items-center gap-1.5 flex-wrap"}>
                  <span>{f.label}</span>
                  {f.required && <span className="text-[#E94545] ml-0.5">*</span>}
                  {f.private && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                      style={{ background: "rgba(34,158,110,0.12)", color: "#1E7E3F" }}
                      data-testid={`field-private-${f.name}`}
                    >
                      🔒 Solo tu
                    </span>
                  )}
                </label>
                {f.type === "select" ? (
                  <select
                    data-testid={`field-${f.name}`}
                    value={data[f.name] || ""}
                    onChange={(e) => setData((d) => ({ ...d, [f.name]: e.target.value }))}
                    className={INPUT_CLS}
                  >
                    {f.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                  </select>
                ) : f.multiline ? (
                  <textarea
                    data-testid={`field-${f.name}`}
                    required={f.required}
                    value={data[f.name] || ""}
                    onChange={(e) => setData((d) => ({ ...d, [f.name]: e.target.value }))}
                    className={`${INPUT_CLS} min-h-[80px]`}
                  />
                ) : (
                  <input
                    data-testid={`field-${f.name}`}
                    type={f.type || "text"}
                    required={f.required}
                    value={data[f.name] || ""}
                    onChange={(e) => setData((d) => ({ ...d, [f.name]: e.target.value }))}
                    className={INPUT_CLS}
                  />
                )}
                {f.private && f.hint && (
                  <div className="mt-1 text-[11px] text-[#7A7A7A] leading-snug">{f.hint}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Shelter-only: adoption settings */}
        {user?.is_shelter && (
          <div
            data-testid="dog-adoption-block"
            className="rounded-3xl p-5 sm:p-6 border bg-[#FFFBF2]"
            style={{ borderColor: "rgba(255,176,32,0.45)", boxShadow: "0 8px 24px -10px rgba(255,176,32,0.18)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C77F00] mb-2 font-semibold">Adozione</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="dog-is-adoptable"
                checked={!!data.is_adoptable}
                onChange={(e) => setData((d) => ({ ...d, is_adoptable: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-[#FFB020]"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1A1A1A]">Questo cane è in cerca di adozione</div>
                <div className="text-[11px] text-[#7A6F58] mt-0.5">
                  Apparirà nel tuo profilo rifugio. Gli utenti potranno candidarsi via ORIO.
                </div>
              </div>
            </label>
            {data.is_adoptable && (
              <>
                <div className="mt-4">
                  <label className={LABEL_CLS}>Storia / descrizione adozione</label>
                  <textarea
                    data-testid="dog-adoption-story"
                    value={data.adoption_story || ""}
                    onChange={(e) => setData((d) => ({ ...d, adoption_story: e.target.value }))}
                    placeholder="Racconta carattere, esigenze, con chi si trova bene. Più dettagli, più candidature giuste."
                    maxLength={1500}
                    className={`${INPUT_CLS} min-h-[110px]`}
                  />
                </div>
                <div className="mt-3">
                  <label className={LABEL_CLS}>Stato</label>
                  <select
                    data-testid="dog-adoption-status"
                    value={data.adoption_status || "available"}
                    onChange={(e) => setData((d) => ({ ...d, adoption_status: e.target.value }))}
                    className={INPUT_CLS}
                  >
                    <option value="available">Disponibile</option>
                    <option value="reserved">Riservato</option>
                    <option value="adopted">Adottato</option>
                  </select>
                </div>

                {/* Extra foto gallery (up to 5 extra beyond primary) */}
                <div className="mt-4">
                  <label className={LABEL_CLS}>Foto extra <span className="text-[10.5px] font-normal text-[#9A9A9A]">(fino a 5)</span></label>
                  <ExtraPhotosPicker
                    photos={data.photos || []}
                    onChange={(next) => setData((d) => ({ ...d, photos: next }))}
                  />
                </div>

                {/* Temperament — multi-select chips */}
                <div className="mt-4">
                  <label className={LABEL_CLS}>Carattere</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: "calm", label: "Tranquillo" },
                      { id: "active", label: "Attivo" },
                      { id: "playful", label: "Giocherellone" },
                      { id: "shy", label: "Timido" },
                      { id: "social", label: "Socievole" },
                      { id: "protective", label: "Protettivo" },
                    ].map((p) => {
                      const on = (data.personality || []).includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`dog-personality-${p.id}`}
                          onClick={() => setData((d) => {
                            const cur = new Set(d.personality || []);
                            on ? cur.delete(p.id) : cur.add(p.id);
                            return { ...d, personality: Array.from(cur) };
                          })}
                          className="px-3 py-1.5 rounded-full text-[12px] font-semibold border transition"
                          style={{
                            background: on ? "#FF7A1A" : "white",
                            borderColor: on ? "#FF7A1A" : "#F0EBE3",
                            color: on ? "white" : "#1A1A1A",
                          }}
                        >{p.label}</button>
                      );
                    })}
                  </div>
                </div>

                {/* Energy level 1..5 */}
                <div className="mt-4">
                  <label className={LABEL_CLS}>Livello energia <span className="text-[10.5px] font-normal text-[#9A9A9A]">(1 = tranquillo · 5 = super attivo)</span></label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`dog-energy-${v}`}
                        onClick={() => setData((d) => ({ ...d, energy_level: v }))}
                        className="h-10 w-10 rounded-full text-[13px] font-bold border transition"
                        style={{
                          background: (data.energy_level || 0) === v ? "#FF7A1A" : "white",
                          borderColor: (data.energy_level || 0) === v ? "#FF7A1A" : "#F0EBE3",
                          color: (data.energy_level || 0) === v ? "white" : "#1A1A1A",
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>

                {/* Compatibility quick 3-toggles */}
                <div className="mt-4">
                  <label className={LABEL_CLS}>Si trova bene con</label>
                  <div className="space-y-2">
                    {[
                      { key: "with_dogs", label: "Altri cani" },
                      { key: "with_kids", label: "Bambini" },
                      { key: "with_cats", label: "Gatti" },
                      { key: "with_strangers", label: "Sconosciuti" },
                    ].map((c) => (
                      <div key={c.key} className="flex items-center justify-between p-2 rounded-lg border border-[#F0EBE3]">
                        <span className="text-[13px] text-[#1A1A1A]">{c.label}</span>
                        <div className="inline-flex rounded-full bg-[#FBF7EF] p-0.5 text-[11px] font-semibold">
                          {["yes", "depends", "no"].map((v) => {
                            const cur = (data.compatibility || {})[c.key];
                            const on = cur === v;
                            const color = v === "yes" ? "#2DBA60" : v === "depends" ? "#F59E0B" : "#E11D48";
                            return (
                              <button
                                key={v}
                                type="button"
                                data-testid={`dog-compat-${c.key}-${v}`}
                                onClick={() => setData((d) => ({
                                  ...d,
                                  compatibility: { ...(d.compatibility || {}), [c.key]: v },
                                }))}
                                className="px-2.5 py-1 rounded-full transition"
                                style={{
                                  background: on ? color : "transparent",
                                  color: on ? "white" : "#7A7A7A",
                                }}
                              >
                                {v === "yes" ? "Sì" : v === "depends" ? "A volte" : "No"}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/app/cani")}
            data-testid="dog-cancel"
            className="px-5 py-3 rounded-full bg-white border border-[#F0EBE3] text-[#5C5C5C] hover:text-[#1A1A1A] hover:bg-[#FAF7F0] text-sm font-medium transition-all"
          >
            Annulla
          </button>
          <button
            data-testid="dog-submit"
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3.5 rounded-full text-white font-semibold transition-all disabled:opacity-50 active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
              boxShadow: "0 10px 28px -8px rgba(255,122,26,0.55)",
            }}
          >
            {loading ? "Salvataggio…" : "Salva passaporto"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ExtraPhotosPicker — max 5 additional photos.
   Each slot: upload button (data URL) with preview + remove. */
function ExtraPhotosPicker({ photos, onChange }) {
  const inputRef = React.useRef(null);
  const add = async (file) => {
    if (!file) return;
    if (photos.length >= 5) return;
    if (file.size > 25 * 1024 * 1024) return alert("File troppo grande (max 25 MB)");
    if (!file.type?.startsWith("image/")) return alert("Formato non supportato");
    try {
      const dataUrl = await compressImage(file, { maxSide: 1280, quality: 0.85 });
      onChange([...photos, dataUrl]);
    } catch {
      alert("Impossibile leggere la foto. Riprova con un'altra.");
    }
  };
  const remove = (idx) => onChange(photos.filter((_, i) => i !== idx));
  return (
    <div className="grid grid-cols-3 gap-2">
      {photos.map((p, i) => (
        <div key={i} className="relative rounded-xl overflow-hidden bg-[#F5EFE6] border border-[#F0EBE3]" style={{ aspectRatio: "1/1" }}>
          <img src={p} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => remove(i)}
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white text-[12px] font-bold flex items-center justify-center">
            ×
          </button>
        </div>
      ))}
      {photos.length < 5 && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="dog-extra-photo-add"
          className="rounded-xl border-2 border-dashed border-[#F0EBE3] bg-white flex items-center justify-center text-[#FF7A1A] text-2xl font-bold"
          style={{ aspectRatio: "1/1" }}
        >+</button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => { add(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
